import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ChevronDown, ChevronRight, Printer } from "lucide-react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { EasyParcelValidationIssue } from "@gaole/shared";
import { formatPickupAddress, formatPickupScheduledAt, formatShippingAddressLines, orderStatusLabel } from "@gaole/shared";
import { api, apiDownload, ApiError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { toast } from "../stores/toast";
import { useShellTabs } from "../stores/shell-tabs";
import { ParcelCard } from "../components/fulfilment/ParcelCard";
import {
  printAllPackingSlips,
  printAllPickGroupSheets,
  printFailedToast,
  printPackingSlip,
  printPickGroupSheet,
} from "../components/fulfilment/PackingListPrint";
import {
  issueSummary,
  parseTrackingLines,
  type DetailTab,
  type PickListDetail,
  type PickListOrderDetail,
} from "../components/fulfilment/types";
import {
  Badge,
  Button,
  Input,
  PageLoader,
  PillSegmentSwitcher,
  statusColor,
  Textarea,
  useConfirm,
} from "../components/ui";

const TAB_LABEL: Record<DetailTab, string> = {
  pick: "Pick",
  pack: "Pack",
  ship: "Ship",
};

export function PickListDetail() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const setActiveTabTitle = useShellTabs((s) => s.setActiveTabTitle);

  const tab = (searchParams.get("tab") as DetailTab) || "pick";
  const setTab = (next: DetailTab) => {
    setSearchParams({ tab: next }, { replace: true });
  };

  const [expandedCustomers, setExpandedCustomers] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [trackingText, setTrackingText] = useState("");
  const [trackingDraft, setTrackingDraft] = useState<Record<string, { trackingNo: string; courier: string }>>({});
  const [downloading, setDownloading] = useState(false);
  const [exportIssues, setExportIssues] = useState<EasyParcelValidationIssue[]>([]);

  const detailQuery = useQuery({
    queryKey: ["fulfilment-picklist", id],
    queryFn: () => api<PickListDetail>(`/fulfilment/picklists/${id}`),
    enabled: !!id,
  });

  const detail = detailQuery.data;
  const pickList = detail?.pickList;

  useEffect(() => {
    if (!pickList) return;
    setActiveTabTitle(`${pickList.code} · ${TAB_LABEL[tab]}`);
  }, [pickList, tab, setActiveTabTitle]);

  useEffect(() => {
    if (detail?.customers.length && expandedCustomers.size === 0) {
      setExpandedCustomers(new Set(detail.customers.map((c) => c.userId)));
      const groupKeys = detail.customers.flatMap((c) => c.addressGroups.map((g) => g.groupKey));
      setExpandedGroups(new Set(groupKeys));
    }
  }, [detail?.customers, expandedCustomers.size]);

  useEffect(() => {
    if (detail?.validationIssues) {
      setExportIssues(detail.validationIssues);
    }
  }, [detail?.validationIssues]);

  useEffect(() => {
    if (!detail?.orders) return;
    setTrackingDraft(
      Object.fromEntries(
        detail.orders
          .filter((o) => o.fulfilmentMethod !== "PICKUP")
          .map((o) => [o.id, { trackingNo: o.trackingNo ?? "", courier: o.courier ?? "" }]),
      ),
    );
  }, [detail?.orders]);

  const completeMutation = useMutation({
    mutationFn: (pickListId: string) => api(`/fulfilment/picklists/${pickListId}/complete`, { method: "POST" }),
    onSuccess: () => {
      toast("success", "Pick list marked packed");
      void queryClient.invalidateQueries({ queryKey: ["fulfilment-picklists"] });
      void queryClient.invalidateQueries({ queryKey: ["fulfilment-picklist", id] });
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Complete failed"),
  });

  const collectMutation = useMutation({
    mutationFn: (orderId: string) => api(`/orders/${orderId}/status`, { body: { status: "DELIVERED" } }),
    onSuccess: () => {
      toast("success", "Order marked collected");
      void queryClient.invalidateQueries({ queryKey: ["fulfilment-picklist", id] });
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Update failed"),
  });

  const trackingMutation = useMutation({
    mutationFn: (entries: { orderNo: string; trackingNo: string; courier?: string }[]) =>
      api<{ results: { orderNo: string; ok: boolean; error?: string }[] }>("/fulfilment/tracking", {
        method: "POST",
        body: { entries },
      }),
    onSuccess: (data) => {
      const failed = data.results.filter((r) => !r.ok);
      if (failed.length === 0) {
        toast("success", "Tracking saved");
        setTrackingText("");
      } else {
        toast("error", `${failed.length} row(s) failed — check order numbers`);
      }
      void queryClient.invalidateQueries({ queryKey: ["fulfilment-picklist", id] });
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Tracking upload failed"),
  });

  const addParcelMutation = useMutation({
    mutationFn: ({ pickListId, orderId }: { pickListId: string; orderId: string }) =>
      api(`/fulfilment/picklists/${pickListId}/orders/${orderId}/parcels`, { method: "POST", body: {} }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["fulfilment-picklist", id] }),
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Add parcel failed"),
  });

  const autoSplitMutation = useMutation({
    mutationFn: ({ pickListId, orderId }: { pickListId: string; orderId: string }) =>
      api(`/fulfilment/picklists/${pickListId}/orders/${orderId}/parcels/auto-split`, { method: "POST" }),
    onSuccess: () => {
      toast("success", "Split into one parcel per item");
      void queryClient.invalidateQueries({ queryKey: ["fulfilment-picklist", id] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Auto-split failed"),
  });

  const allOrders = useMemo(
    () =>
      detail?.customers.flatMap((c) => c.addressGroups.flatMap((g) => g.orders)) ?? [],
    [detail?.customers],
  );

  const deliveryOrders = useMemo(
    () => detail?.orders.filter((o) => o.fulfilmentMethod !== "PICKUP") ?? [],
    [detail?.orders],
  );

  const pickupOrders = useMemo(
    () => detail?.orders.filter((o) => o.fulfilmentMethod === "PICKUP") ?? [],
    [detail?.orders],
  );

  async function downloadEasyParcel(pickListId: string, code: string) {
    setDownloading(true);
    setExportIssues([]);
    try {
      await apiDownload(`/fulfilment/picklists/${pickListId}/easyparcel.xlsx`, `easyparcel-${code}.xlsx`);
      toast("success", "EasyParcel file downloaded");
    } catch (err) {
      if (err instanceof ApiError) {
        const details = err.details as { validationIssues?: EasyParcelValidationIssue[] } | undefined;
        if (details?.validationIssues?.length) {
          setExportIssues(details.validationIssues);
        }
        toast("error", err.message);
      } else {
        toast("error", "Download failed");
      }
    } finally {
      setDownloading(false);
    }
  }

  function refreshDetail() {
    void queryClient.invalidateQueries({ queryKey: ["fulfilment-picklist", id] });
  }

  function toggleCustomer(userId: string) {
    setExpandedCustomers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  function toggleGroup(groupKey: string) {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  }

  const incompleteCount = detail?.validationIssues.length ?? 0;
  const sidebarTabs = useMemo(
    () => [
      { value: "pick" as const, label: "Pick" },
      { value: "pack" as const, label: "Pack", badge: incompleteCount > 0 ? incompleteCount : undefined },
      { value: "ship" as const, label: "Ship" },
    ],
    [incompleteCount],
  );

  const issuesToShow = exportIssues.length ? exportIssues : (detail?.validationIssues ?? []);

  if (detailQuery.isLoading) {
    return <PageLoader />;
  }

  if (!detail || !pickList) {
    return (
      <div className="mx-auto max-w-2xl py-12 text-center">
        <p className="text-muted">Pick list not found.</p>
        <Link to="/fulfilment" className="mt-4 inline-block text-sm text-brand-pink hover:underline">
          Back to fulfilment
        </Link>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/fulfilment"
            className="mb-2 inline-flex items-center gap-1 text-xs text-muted hover:text-foreground"
          >
            <ArrowLeft size={14} />
            Fulfilment
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-display text-xl font-bold">{pickList.code}</h1>
            <Badge color={statusColor(pickList.status)}>{pickList.status}</Badge>
          </div>
          <p className="mt-1 text-xs text-muted">
            Created {fmtDate(pickList.createdAt)} · {detail.orders.length} orders · {detail.pickGroups.length} pick
            groups
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {tab === "pick" && detail.pickGroups.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (!printAllPickGroupSheets(detail.pickGroups, pickList.code)) {
                  toast("error", printFailedToast());
                }
              }}
            >
              <Printer size={14} className="mr-1" />
              Print all pick sheets
            </Button>
          )}
          {tab === "pack" && allOrders.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (!printAllPackingSlips(allOrders, pickList.code)) {
                  toast("error", printFailedToast());
                }
              }}
            >
              <Printer size={14} className="mr-1" />
              Print all packing slips
            </Button>
          )}
          {pickList.status !== "COMPLETED" && (
            <Button
              size="sm"
              disabled={completeMutation.isPending}
              onClick={() =>
                confirm({
                  title: "Mark packed?",
                  message: "All orders on this pick list will move to PACKED status.",
                  confirmLabel: "Mark packed",
                  action: () => completeMutation.mutate(pickList.id),
                })
              }
            >
              Mark packed
            </Button>
          )}
        </div>
      </div>

      <div className="shrink-0">
        <PillSegmentSwitcher options={sidebarTabs} value={tab} onChange={setTab} />
      </div>

      <div className="page-scroll min-h-0 flex-1">
      {tab === "pick" && (
        <section className="space-y-3">
          {detail.pickGroups.length === 0 ? (
            <p className="text-sm text-muted">No pick groups on this list.</p>
          ) : (
            detail.pickGroups.map((group) => {
              const addressLines = formatShippingAddressLines(group.shippingAddress);
              return (
                <div key={group.groupKey} className="rounded-2xl border border-line bg-surface p-4">
                  <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold">{group.displayName}</p>
                      <p className="text-xs text-muted">{group.email}</p>
                      {group.fulfilmentMethod === "PICKUP" && (
                        <div className="mt-1">
                          <Badge color="blue">Store pickup</Badge>
                        </div>
                      )}
                      <div className="mt-1 text-xs text-muted">
                        {group.fulfilmentMethod === "PICKUP" ? (
                          <>
                            <p>{group.addressSummary}</p>
                            {group.pickupScheduledAt && (
                              <p className="mt-1 font-medium text-brand-pink">
                                Pickup: {formatPickupScheduledAt(group.pickupScheduledAt)}
                              </p>
                            )}
                          </>
                        ) : (
                          addressLines.map((line) => <div key={line}>{line}</div>)
                        )}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {group.orders.map((o) => (
                          <span
                            key={o.id}
                            className="rounded-md bg-hover px-2 py-0.5 font-mono text-[10px] text-muted"
                          >
                            {o.orderNo}
                          </span>
                        ))}
                      </div>
                      <p className="mt-1 text-[10px] text-muted">
                        {group.orders.length} order{group.orders.length === 1 ? "" : "s"} · {group.pickItems.length}{" "}
                        SKU{group.pickItems.length === 1 ? "" : "s"}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (!printPickGroupSheet(group, pickList.code)) {
                          toast("error", printFailedToast());
                        }
                      }}
                    >
                      <Printer size={14} className="mr-1" />
                      {group.fulfilmentMethod === "PICKUP" ? "Print pickup sheet" : "Print pick sheet"}
                    </Button>
                  </div>
                  <ul className="space-y-1.5">
                    {group.pickItems.map((item) => (
                      <li
                        key={item.sku}
                        className="flex items-center justify-between gap-2 rounded-xl border border-line bg-hover/50 px-3 py-2 text-xs"
                      >
                        <div className="min-w-0">
                          <p className="truncate font-medium">{item.name}</p>
                          <p className="font-mono text-[10px] text-muted">{item.sku}</p>
                        </div>
                        <span className="shrink-0 font-display font-semibold">×{item.quantity}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })
          )}
        </section>
      )}

      {tab === "pack" && (
        <section className="space-y-2">
          {detail.packingBoxes.length === 0 && (
            <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
              No packing box presets yet. Add them under Sales → Packing boxes for faster packing.
            </p>
          )}
          {detail.customers.map((customer) => {
            const customerExpanded = expandedCustomers.has(customer.userId);
            const orderCount = customer.addressGroups.reduce((n, g) => n + g.orders.length, 0);
            return (
              <div key={customer.userId} className="rounded-2xl border border-line bg-surface">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-4 py-3 text-left"
                  onClick={() => toggleCustomer(customer.userId)}
                >
                  {customerExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold">{customer.displayName}</p>
                    <p className="truncate text-xs text-muted">{customer.email}</p>
                  </div>
                  <span className="text-xs text-muted">
                    {customer.addressGroups.length} address{customer.addressGroups.length === 1 ? "" : "es"} ·{" "}
                    {orderCount} orders
                  </span>
                </button>
                {customerExpanded && (
                  <div className="space-y-3 border-t border-line px-4 py-3">
                    {customer.addressGroups.map((group) => {
                      const groupExpanded = expandedGroups.has(group.groupKey);
                      const addressLines = formatShippingAddressLines(group.shippingAddress);
                      return (
                        <div key={group.groupKey} className="rounded-xl border border-line bg-hover/30">
                          <button
                            type="button"
                            className="flex w-full items-center gap-2 px-3 py-2 text-left"
                            onClick={() => toggleGroup(group.groupKey)}
                          >
                            {groupExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-semibold">{group.addressSummary}</p>
                              {addressLines.slice(0, 2).map((line) => (
                                <p key={line} className="truncate text-[10px] text-muted">
                                  {line}
                                </p>
                              ))}
                            </div>
                            <span className="text-xs text-muted">{group.orders.length} orders</span>
                          </button>
                          {groupExpanded && (
                            <div className="space-y-3 border-t border-line px-3 py-3">
                              {group.orders.map((order) => (
                                <OrderPackSection
                                  key={order.id}
                                  order={order}
                                  packingBoxes={detail.packingBoxes}
                                  onAddParcel={() =>
                                    addParcelMutation.mutate({ pickListId: pickList.id, orderId: order.id })
                                  }
                                  onAutoSplit={() =>
                                    autoSplitMutation.mutate({ pickListId: pickList.id, orderId: order.id })
                                  }
                                  onPrint={() => {
                                    if (!printPackingSlip(order, pickList.code)) {
                                      toast("error", printFailedToast());
                                    }
                                  }}
                                  addParcelPending={addParcelMutation.isPending}
                                  autoSplitPending={autoSplitMutation.isPending}
                                  onSaved={refreshDetail}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </section>
      )}

      {tab === "ship" && (
        <div className="space-y-4">
          {deliveryOrders.length > 0 && !detail.exportReady && issuesToShow.length > 0 && (
            <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-100">
              <p className="mb-1 font-semibold">Complete all parcels before exporting</p>
              <ul className="list-inside list-disc space-y-0.5 text-red-200/90">
                {issuesToShow.map((issue, i) => (
                  <li key={`${issue.orderNo}-${issue.parcelId ?? i}`}>{issueSummary(issue)}</li>
                ))}
              </ul>
            </div>
          )}

          {deliveryOrders.length > 0 && (
            <>
              <Button
                size="sm"
                disabled={downloading || !detail.exportReady}
                title={detail.exportReady ? undefined : "Complete parcel dimensions and weight on the Pack tab"}
                onClick={() => void downloadEasyParcel(pickList.id, pickList.code)}
              >
                Download EasyParcel
              </Button>

              <section>
                <h4 className="mb-2 font-display text-xs font-semibold uppercase tracking-wider text-muted">
                  Delivery orders
                </h4>
                <ul className="space-y-3">
                  {deliveryOrders.map((order) => {
                    const draft = trackingDraft[order.id] ?? { trackingNo: "", courier: "" };
                    return (
                      <li key={order.id} className="rounded-xl border border-line bg-surface px-3 py-3 text-xs">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono font-semibold">{order.orderNo}</span>
                          <Badge color={statusColor(order.status)}>{orderStatusLabel(order.status)}</Badge>
                        </div>
                        <p className="mt-0.5 text-muted">{order.customer}</p>
                        {(order.trackingNo || order.courier) && (
                          <p className="mt-1 font-mono text-foreground/70">
                            {order.courier ? `${order.courier} · ` : ""}
                            {order.trackingNo ?? ""}
                          </p>
                        )}
                        <div className="mt-3 flex flex-wrap items-end gap-2">
                          <Input
                            label="Tracking number"
                            className="min-w-[140px] flex-1"
                            value={draft.trackingNo}
                            onChange={(e) =>
                              setTrackingDraft((prev) => ({
                                ...prev,
                                [order.id]: { ...draft, trackingNo: e.target.value },
                              }))
                            }
                            placeholder="EasyParcel AWB"
                          />
                          <Input
                            label="Courier / provider"
                            className="min-w-[120px] flex-1"
                            value={draft.courier}
                            onChange={(e) =>
                              setTrackingDraft((prev) => ({
                                ...prev,
                                [order.id]: { ...draft, courier: e.target.value },
                              }))
                            }
                            placeholder="Pos Laju, J&T…"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={trackingMutation.isPending || draft.trackingNo.trim().length < 4}
                            onClick={() => {
                              trackingMutation.mutate([
                                {
                                  orderNo: order.orderNo,
                                  trackingNo: draft.trackingNo.trim(),
                                  courier: draft.courier.trim() || undefined,
                                },
                              ]);
                            }}
                          >
                            Save
                          </Button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>

              <section>
                <h4 className="mb-2 font-display text-xs font-semibold uppercase tracking-wider text-muted">
                  Bulk tracking entry
                </h4>
                <Textarea
                  label="Paste tracking numbers"
                  hint="One per line: ORDER_NO, TRACKING_NO, COURIER (courier optional)"
                  rows={5}
                  placeholder={"ORD-0001, EP123456789MY, Pos Laju\nORD-0002 EP987654321MY J&T"}
                  value={trackingText}
                  onChange={(e) => setTrackingText(e.target.value)}
                />
                <Button
                  className="mt-2"
                  size="sm"
                  variant="outline"
                  disabled={trackingMutation.isPending || !trackingText.trim()}
                  onClick={() => {
                    const entries = parseTrackingLines(trackingText);
                    if (entries.length === 0) {
                      toast("error", "Enter at least one valid line");
                      return;
                    }
                    trackingMutation.mutate(entries);
                  }}
                >
                  Save
                </Button>
              </section>
            </>
          )}

          {pickupOrders.length > 0 && (
            <section>
              <h4 className="mb-2 font-display text-xs font-semibold uppercase tracking-wider text-muted">
                Store pickup
              </h4>
              <ul className="space-y-2">
                {pickupOrders.map((order) => (
                  <li key={order.id} className="rounded-xl border border-line bg-surface px-3 py-2 text-xs">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono font-semibold">{order.orderNo}</span>
                          <Badge color="blue">Pickup</Badge>
                          <Badge color={statusColor(order.status)}>{orderStatusLabel(order.status)}</Badge>
                        </div>
                        <p className="mt-0.5 text-muted">{order.customer}</p>
                        {order.pickupDetails && (
                          <div className="mt-2 text-muted">
                            <p className="font-medium text-foreground">{order.pickupDetails.name}</p>
                            <p>{formatPickupAddress(order.pickupDetails)}</p>
                            {order.pickupDetails.instructions && (
                              <p className="mt-1">{order.pickupDetails.instructions}</p>
                            )}
                            <p className="mt-1 font-medium text-brand-pink">
                              {formatPickupScheduledAt(order.pickupDetails.scheduledAt)}
                            </p>
                          </div>
                        )}
                      </div>
                      {order.status === "PACKED" && (
                        <Button
                          size="sm"
                          disabled={collectMutation.isPending}
                          onClick={() => collectMutation.mutate(order.id)}
                        >
                          Mark collected
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {deliveryOrders.length === 0 && pickupOrders.length === 0 && (
            <p className="text-sm text-muted">No orders on this pick list.</p>
          )}
        </div>
      )}

      </div>

      {dialog}
    </div>
  );
}

function OrderPackSection({
  order,
  packingBoxes,
  onAddParcel,
  onAutoSplit,
  onPrint,
  addParcelPending,
  autoSplitPending,
  onSaved,
}: {
  order: PickListOrderDetail;
  packingBoxes: PickListDetail["packingBoxes"];
  onAddParcel: () => void;
  onAutoSplit: () => void;
  onPrint: () => void;
  addParcelPending: boolean;
  autoSplitPending: boolean;
  onSaved: () => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <span className="font-mono text-xs font-semibold">{order.orderNo}</span>
          <ul className="mt-1 space-y-0.5 text-xs text-muted">
            {order.items.map((item) => (
              <li key={item.id}>
                {item.quantity}× {item.name}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-wrap gap-1">
          <Button variant="outline" size="sm" onClick={onPrint}>
            <Printer size={14} className="mr-1" />
            Print
          </Button>
          <Button variant="outline" size="sm" disabled={addParcelPending} onClick={onAddParcel}>
            Add parcel
          </Button>
          {order.items.length > 1 && (
            <Button variant="outline" size="sm" disabled={autoSplitPending} onClick={onAutoSplit}>
              Split items
            </Button>
          )}
        </div>
      </div>
      <div className="space-y-2">
        {order.parcels.map((parcel) => (
          <ParcelCard
            key={parcel.id}
            parcel={parcel}
            order={order}
            boxes={packingBoxes}
            onSaved={onSaved}
          />
        ))}
      </div>
    </div>
  );
}
